import { useState } from 'react';
import { SpeakerWaveIcon } from '@heroicons/react/24/outline';

import type { MessageMenage } from '../../api/useMessagesMenage';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import Text from '../../components/Text';
import { heureBulle } from '../../helpers/conversation';
import { lireAVoixHaute, PICTOS_REPONSES } from '../../helpers/messagesMenage';
import { t } from '../../i18n/langue';

interface BulleMenageProps {
    message: MessageMenage;
    /** Les gestes offerts sur ce message (menu sous la bulle). */
    actions?: { libelle: string; onPress: () => void }[];
}

/**
 * Un message de la messagerie ménage (Q8) : la traduction en gros, l'original
 * replié dessous ; le vocal reste écoutable (Q9) ; 🔊 lit le texte avec la voix
 * du téléphone (Q10). Les miens à droite, en couleur de marque.
 */
export default function BulleMenage({ message, actions = [] }: BulleMenageProps) {
    const [originalOuvert, setOriginalOuvert] = useState(false);
    const [sansVoix, setSansVoix] = useState(false);
    const Picto = message.quick_reply_key ? PICTOS_REPONSES[message.quick_reply_key] : null;
    const texte = message.quick_reply_key ? t(`messagesMenage.rapides.${message.quick_reply_key}`) : message.text;

    const handleLire = () => {
        if (texte) setSansVoix(!lireAVoixHaute(texte));
    };

    return (
        <div className={`flex flex-col gap-1 ${message.is_mine ? 'items-end' : 'items-start'}`}>
            {!message.is_mine && message.author && (
                <Text variante="petit" ton="attenue" as="span">
                    {message.author.firstname}
                </Text>
            )}
            <div
                className={`flex max-w-[85%] flex-col gap-2 rounded-2xl px-3 py-2 ${
                    message.is_mine ? 'bg-main-color-light' : 'bg-white'
                } ${message.is_urgent ? 'border-2 border-alert-text-color' : ''}`}
            >
                {message.is_urgent && (
                    <Text variante="petit" ton="danger" as="span" className="font-semibold">
                        🔴 {t('messagesMenage.bulle.urgent')}
                    </Text>
                )}
                {message.reply_to?.text && (
                    <Text variante="petit" ton="attenue" as="span" className="border-l-2 border-border-color-light pl-2">
                        {t('messagesMenage.bulle.reponseA', { texte: message.reply_to.text })}
                    </Text>
                )}

                {message.hidden ? (
                    <Text ton="attenue" className="italic">
                        {t('messagesMenage.bulle.masque')}
                    </Text>
                ) : (
                    <>
                        {message.photo_url && (
                            <img src={message.photo_url} alt={t('messagesMenage.bulle.photo')} className="max-h-64 rounded-xl object-cover" />
                        )}
                        {message.audio_url && <audio controls src={message.audio_url} className="w-full max-w-[16rem]" />}
                        {texte && (
                            <div className="flex items-start gap-2">
                                {Picto && <Picto aria-hidden className="mt-0.5 h-7 w-7 shrink-0 text-main-color" />}
                                <Text className={`whitespace-pre-line ${Picto ? 'text-lg font-semibold' : 'text-lg'}`}>{texte}</Text>
                                <IconButton libelle={t('messagesMenage.bulle.lireAVoixHaute')} onPress={handleLire} className="-mr-2 -mt-2 shrink-0">
                                    <SpeakerWaveIcon className="h-5 w-5 text-text-color" />
                                </IconButton>
                            </div>
                        )}
                        {sansVoix && (
                            <Text variante="petit" ton="attenue">
                                {t('messagesMenage.bulle.pasDeVoix')}
                            </Text>
                        )}
                        {message.translation_pending && (
                            <Text variante="petit" ton="attenue">
                                {t('messagesMenage.bulle.traductionEnCours')}
                            </Text>
                        )}
                        {message.translation_unavailable && (
                            <Text variante="petit" ton="attenue">
                                {t('messagesMenage.bulle.sansTraduction')}
                            </Text>
                        )}
                        {message.original && (
                            <>
                                <Button
                                    libelle={originalOuvert ? t('messagesMenage.bulle.masquerOriginal') : t('messagesMenage.bulle.voirOriginal')}
                                    variante="lien"
                                    alignement="debut"
                                    espacement="aucun"
                                    onPress={() => setOriginalOuvert((ouvert) => !ouvert)}
                                />
                                {originalOuvert && (
                                    <Text variante="petit" ton="attenue" className="whitespace-pre-line">
                                        {message.original}
                                    </Text>
                                )}
                            </>
                        )}
                        {message.room && (
                            <Text variante="petit" ton="marque" as="span">
                                {t('messagesMenage.bulle.chambre', { nom: message.room.name })}
                            </Text>
                        )}
                    </>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3">
                <Text variante="petit" ton="attenue" as="span">
                    {heureBulle(message.created_at)}
                    {message.is_mine && message.read_count !== undefined && (
                        <>
                            {' · '}
                            {message.read_count > 0
                                ? t('messagesMenage.bulle.luPar', { n: message.read_count })
                                : t('messagesMenage.bulle.nonLu')}
                        </>
                    )}
                </Text>
                {actions.map((action) => (
                    <Button key={action.libelle} libelle={action.libelle} variante="lien" espacement="aucun" onPress={action.onPress} />
                ))}
            </div>
        </div>
    );
}
